import { memo, useEffect, useRef } from "react";
import { arrayOf, bool, func, node, number, object, oneOf, oneOfType, string } from "prop-types";
import { ClickAwayListener, Fade, Paper, Portal } from "@material-ui/core";
import {
  FloatingArrow,
  flip,
  offset,
  shift,
  useFloating,
  autoUpdate,
  arrow,
} from "@floating-ui/react";
import { pageOverflowMiddleware } from "./middlewares/pageOverflowMiddleware";
import useStyle from "./PopMenu.style";
import classnames from "classnames";

export const PopMenu = memo(({
  id,
  show,
  children,
  placement="bottom",
  anchor,
  onClickAway,
  showArrow,
  className,
  style,
  offset: popperOffset=8,
  onMouseEnter,
  onMouseLeave,
  pinToViewport,
  noFlip,
  clickAwayMouseEvent="onMouseDown",
}) => {
  const classes = useStyle();

  const arrowRef = useRef(null);
  
  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: show,
    whileElementsMounted: autoUpdate,
    middleware: [
      ...(noFlip ? [] : [flip()]),
      shift(),
      offset(popperOffset),
      arrow({
        element: arrowRef,
      }),
      ...(pinToViewport ? [pageOverflowMiddleware] : []),
    ],
    elements: {
      reference: anchor?.isVirtual ? undefined : anchor,
    }
  });
  
  /** @note: for handling virtual anchor elements */
  useEffect(() => {
    if(anchor)
      refs.setPositionReference(anchor);
  }, [anchor]);
  
  // useEffect(() => {
  //   if(anchor?.isVirtual && refs.reference?.current && refs.floating?.current ) {
  //     return autoUpdate(
  //       refs.reference,
  //       refs.floating,
  //       update,
  //     );
  //   }
  // }, [anchor, refs, update]);
  
  const handleClickAway = (e) => {
    if(anchor.contains?.(e.target))
      return;
    onClickAway?.(e);
  }
  
  if(!show)
    return null;
  
  return <Portal>
    <div
      id={id}
      ref={refs.setFloating}
      style={floatingStyles}
      className={classnames(classes.root, !show && "hidden")}
      role="presentation"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {
        onClickAway
          ? <ClickAwayListener onClickAway={handleClickAway} mouseEvent={clickAwayMouseEvent}>
            <Fade in={show}>
              <Paper
                className={classnames(classes.content, className)}
                style={style}
              >
                {
                  showArrow &&
                  <FloatingArrow
                    ref={arrowRef}
                    context={context}
                    className={classes.arrow}
                  />
                }
                { children }
              </Paper>
            </Fade>
          </ClickAwayListener>
          : <Fade in={show}>
            <Paper
              className={classnames(classes.content, className)}
              style={style}
            >
              {
                showArrow &&
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  className={classes.arrow}
                />
              }
              {children}
            </Paper>
          </Fade>
      }
    </div>
  </Portal>
});

PopMenu.propTypes = {
  id: string,
  show: bool,
  anchor: object,
  children: oneOfType([arrayOf(node), node]).isRequired,
  onClickAway: func,
  clickAwayMouseEvent: oneOf(["onMouseDown", "onClick"]),
  showArrow: bool,
  placement: oneOf([
    "top",
    "top-start",
    "top-end",
    "bottom-start",
    "bottom",
    "bottom-end",
    "left-start",
    "left",
    "left-end",
    "right-start",
    "right",
    "right-end",
  ]),
  className: string,
  style: object,
  offset: number,
  noAutofocus: bool,
  onMouseEnter: func,
  onMouseLeave: func,
  pinToViewport: bool,
  noFlip: bool,
};